import React, { useState } from 'react';
import adminService from '../../services/api/admin';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import { 
  User, 
  Mail, 
  Phone, 
  Shield, 
  Ban, 
  XCircle, 
  Search, 
  Edit2, 
  Trash2, 
  ShieldCheck, 
  Users, 
  ChevronLeft, 
  ChevronRight, 
  UserCheck, 
  UserX, 
  CheckCircle2,
  Settings,
  Save,
  X
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { useConfirm } from '../../hooks/useConfirm';
import { getImageUrl } from '../../config/apiConfig';

const UsersManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const toast = useToast();

  // جلب المستخدمين (المنطق الأصلي كاملاً)
const { data: users, isLoading, error } = useQuery(
  ['users', { searchTerm, selectedRole, selectedStatus, currentPage }],
  () =>
    adminService.getUsers({
      search: searchTerm,
      role: selectedRole,
      status: selectedStatus,
      page: currentPage,
      limit: 10
    }),
  { keepPreviousData: true }
);
  // Mutation لتحديث حالة المستخدم (نشط / غير نشط)
  const updateStatusMutation = useMutation(
    async ({ userId, status, isActive }) => {
      const response = await adminService.updateUserStatus(userId, { status, isActive });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم تحديث حالة المستخدم بنجاح');
        queryClient.invalidateQueries(['users']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل تحديث حالة المستخدم');
      }
    }
  );

  // Mutation لحذف المستخدم
  const deleteUserMutation = useMutation(
    async (userId) => {
      const response = await adminService.deleteUser(userId);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم حذف المستخدم من النظام بنجاح');
        queryClient.invalidateQueries(['users']);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل حذف المستخدم');
      }
    }
  );

  const handleStatusUpdate = (userId, currentStatus, isActive) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    updateStatusMutation.mutate({ userId, status: newStatus, isActive: !isActive });
  };

  const handleDeleteUser = async (userId) => {
    const confirmed = await confirm({
      title: 'تأكيد الحذف النهائي',
      message: 'هل أنت متأكد من حذف هذا المستخدم؟ لا يمكن التراجع عن هذا الإجراء وسيتم مسح كافة سجلاته.',
      type: 'danger'
    });
    
    if (confirmed) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'مدير النظام';
      case 'teacher': return 'مدرس معتمد';
      case 'student': return 'طالب';
      default: return role;
    }
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-50 text-purple-700 border-purple-100';
      case 'teacher': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'student': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      default: return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getStatusColor = (status, isActive) => {
    if (!isActive) return 'bg-rose-50 text-rose-700 border-rose-100';
    return status === 'active' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-amber-50 text-amber-700 border-amber-100';
  };

  if (isLoading && !users) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري جلب سجلات المستخدمين...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <ShieldCheck className="text-primary-600" size={32} />
                إدارة المستخدمين
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                <Users size={18} className="text-emerald-500" />
                أنت الآن تشرف على {users?.pagination?.total || 0} مستخدم في المنصة
            </p>
          </div>
          
          <div className="bg-primary-600 text-white px-8 py-4 rounded-2xl shadow-xl shadow-primary-100 flex items-center gap-4">
              <div className="bg-white/20 p-2 rounded-xl"><UserCheck size={24} /></div>
              <div>
                  <p className="text-[10px] font-black uppercase opacity-80">المستخدمين النشطين</p>
                  <p className="text-2xl font-black leading-none">{users?.pagination?.total || 0}</p>
              </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters Toolbar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          
          {/* Search Box */}
          <div className="relative md:col-span-6 group">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن مستخدم بالاسم أو البريد الإلكتروني..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-12 pl-4 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />
          </div>
          
          {/* Role Filter */}
          <div className="md:col-span-3">
            <div className="relative">
                <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                <option value="">جميع الأدوار</option>
                <option value="admin">مدراء النظام</option>
                <option value="teacher">المدرسين المعتمدين</option>
                <option value="student">طلاب العلم</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90" size={16} />
            </div>
          </div>
          
          {/* Status Filter */}
          <div className="md:col-span-3">
            <div className="relative">
                <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                <option value="">جميع الحالات</option>
                <option value="active">الحسابات النشطة</option>
                <option value="inactive">الحسابات المحظورة</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90" size={16} />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table Area */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">
        {error ? (
          <div className="py-20 text-center space-y-4">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <p className="text-red-600 font-bold text-lg">حدث خطأ أثناء محاولة تحميل المستخدمين</p>
            <button onClick={() => window.location.reload()} className="bg-gray-100 px-8 py-2 rounded-xl font-bold">تحديث الصفحة</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-50">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">هوية المستخدم</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">البريد الرسمي</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">رتبة الحساب</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">حالة النشاط</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">تاريخ الانضمام</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-50">
                {users?.users?.map((user) => (
                  <tr key={user.id} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className="relative">
                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center text-white font-black shadow-lg shadow-primary-100 group-hover/row:scale-110 transition-transform overflow-hidden">
                            {user.avatar ? (
                              <img src={getImageUrl(user.avatar)} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-xl">{user.name?.charAt(0).toUpperCase()}</span>
                            )}
                            </div>
                            <div className={`absolute -bottom-1 -left-1 w-4 h-4 rounded-full border-2 border-white shadow-sm ${user.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        </div>
                        <div>
                          <div className="text-base font-black text-gray-900 group-hover/row:text-primary-600 transition-colors">{user.name}</div>
                          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">معرف النظام: #{user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-600 group-hover/row:text-gray-900 transition-colors">
                        <Mail size={14} className="text-primary-500" />
                        {user.email}
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase border tracking-widest ${getRoleBadgeStyle(user.role)}`}>
                        <Shield size={12} />
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black border tracking-widest ${getStatusColor(user.status, user.is_active)}`}>
                        {user.is_active ? <CheckCircle2 size={12} /> : <Ban size={12} />}
                        {user.is_active ? 'نشط ومفعل' : 'محظور / معطل'}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                        <div className="flex flex-col">
                            <span className="text-xs font-black text-gray-700">{getArabicDate(user.created_at)}</span>
                            <span className="text-[10px] font-bold text-gray-400 uppercase mt-0.5 tracking-tighter">عضوية رسمية</span>
                        </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-left">
                      <div className="flex items-center justify-end gap-3 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-indigo-600 rounded-xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                          title="تعديل البيانات"
                        >
                          <Edit2 size={16} />
                        </button>
                        
                        <button
                          onClick={() => handleStatusUpdate(user.id, user.status, user.is_active)}
                          className={`w-10 h-10 flex items-center justify-center border rounded-xl transition-all shadow-sm ${
                            user.is_active 
                            ? 'bg-white border-amber-100 text-amber-600 hover:bg-amber-600 hover:text-white' 
                            : 'bg-white border-green-100 text-green-600 hover:bg-green-600 hover:text-white'
                          }`}
                          title={user.is_active ? "حظر المستخدم" : "تفعيل المستخدم"}
                        >
                          {user.is_active ? <UserX size={18} /> : <UserCheck size={18} />}
                        </button>
                        
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-rose-100 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                          title="حذف نهائي"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modern Pagination Section */}
      {users?.pagination && (
        <div className="bg-white rounded-[2rem] shadow-sm p-6 border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-sm font-bold text-gray-500">
              عرض <span className="text-primary-600">{users?.pagination?.from}</span> إلى <span className="text-primary-600">{users?.pagination?.to}</span> من <span className="font-black text-gray-900">{users?.pagination?.total}</span> مستخدم مسجل
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-3 rounded-xl border border-gray-100 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-primary-600"
              >
                <ChevronRight size={20} />
              </button>
              
              <div className="flex items-center gap-1.5">
                  <span className="bg-primary-600 text-white w-10 h-10 flex items-center justify-center rounded-xl font-black text-sm shadow-lg shadow-primary-100">
                    {currentPage}
                  </span>
                  <span className="text-gray-400 px-1 font-bold text-xs">من</span>
                  <span className="bg-gray-100 text-gray-600 w-10 h-10 flex items-center justify-center rounded-xl font-black text-sm">
                    {users?.pagination?.lastPage}
                  </span>
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(users?.pagination?.lastPage, prev + 1))}
                disabled={currentPage === users?.pagination?.lastPage}
                className="p-3 rounded-xl border border-gray-100 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-primary-600"
              >
                <ChevronLeft size={20} />
              </button>
            </div>
        </div>
      )}

      {/* Edit User Modern Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-lg w-full relative overflow-hidden">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between p-8 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-primary-100">
                    <Settings size={24} />
                </div>
                <div>
                    <h3 className="text-xl font-black text-gray-900 leading-none">تحديث ملف المستخدم</h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-1.5">تعديل الصلاحيات والبيانات الأساسية</p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100 shadow-sm"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-8 lg:p-10 space-y-8">
              <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">الاسم الكامل للمستخدم</label>
                    <div className="relative">
                        <input
                        type="text"
                        defaultValue={selectedUser.name}
                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900"
                        />
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">عنوان البريد الإلكتروني</label>
                    <div className="relative">
                        <input
                        type="email"
                        defaultValue={selectedUser.email}
                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900"
                        />
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">دور المستخدم (Role)</label>
                        <div className="relative">
                            <select
                                defaultValue={selectedUser.role}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-700 appearance-none cursor-pointer"
                            >
                                <option value="admin">مدير النظام</option>
                                <option value="teacher">مدرس</option>
                                <option value="student">طالب</option>
                            </select>
                            <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 -rotate-90" size={16} />
                        </div>
                    </div>
                    
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">رقم الهاتف</label>
                        <div className="relative">
                            <input
                                type="tel"
                                defaultValue={selectedUser.phone}
                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-900"
                            />
                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                        </div>
                    </div>
                  </div>
              </div>
              
              <div className="pt-6 grid grid-cols-2 gap-4">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-4 border border-gray-100 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"
                >
                  تجاهل
                </button>
                <button
                  className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                  <Save size={18} /> حفظ التعديلات
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog Component */}
      <ConfirmDialogComponent />
    </div>
  );
};

export default UsersManagement;